/**
 * 与后端 SignInterceptor 保持一致的签名工具。
 * 签名原文：method + "\n" + uri + "\n" + query + "\n" + timestamp + "\n" + nonce
 * 算法：HMAC-SHA256，结果为标准 Base64（非 URL Safe）。
 */

function bytesToBase64(bytes) {
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

function randomNonce() {
  if (window.crypto && window.crypto.getRandomValues) {
    const arr = new Uint8Array(16)
    window.crypto.getRandomValues(arr)
    return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`
}

/**
 * 将 axios config.url 与 baseURL 组合成与后端 request.getRequestURI() 一致的路径。
 * 后端 context-path=/yq-admin，所以 URI 形如 /yq-admin/sys-user/page
 */
export function resolveRequestUri(config, baseURL) {
  const base = (baseURL || '').replace(/\/$/, '')
  let path = config.url || ''

  // 完整 URL 时截取 pathname
  if (/^https?:\/\//i.test(path)) {
    try {
      const u = new URL(path)
      return u.pathname
    } catch (e) {
      // ignore
    }
  }

  // 相对路径
  if (!path.startsWith('/')) path = `/${path}`
  // 若已包含 base 前缀则直接返回
  if (base && path.startsWith(base)) return path
  return `${base}${path}`
}

/**
 * 构建 query 字符串（不含 ?），与 request.getQueryString() 对齐。
 * 尽量使用 axios 序列化后的 params。
 */
export function resolveQueryString(config) {
  // 若 url 上已带 query，优先取它（与后端 RequestURI+QueryString 一致）
  const url = config.url || ''
  const qIndex = url.indexOf('?')
  if (qIndex >= 0) {
    return url.slice(qIndex + 1)
  }

  const params = config.params
  if (!params || typeof params !== 'object') return ''

  const parts = []
  Object.keys(params).forEach(key => {
    const val = params[key]
    if (val === undefined || val === null) return
    if (Array.isArray(val)) {
      val.forEach(item => {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`)
      })
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
    }
  })
  return parts.join('&')
}

export async function hmacSha256Base64(source, secret) {
  const enc = new TextEncoder()
  const key = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const signature = await window.crypto.subtle.sign('HMAC', key, enc.encode(source))
  return bytesToBase64(new Uint8Array(signature))
}

/**
 * 为 axios 请求生成签名请求头。
 * @returns {{ 'X-Timestamp': string, 'X-Nonce': string, 'X-Sign': string }}
 */
export async function buildSignHeaders(config, baseURL, signSecret) {
  const method = (config.method || 'get').toUpperCase()
  // 签名用的 URI 不带 query（query 单独一段）
  let uri = resolveRequestUri(config, baseURL)
  const qIndex = uri.indexOf('?')
  if (qIndex >= 0) uri = uri.slice(0, qIndex)

  const query = resolveQueryString(config)
  const timestamp = String(Date.now())
  const nonce = randomNonce()
  const source = `${method}\n${uri}\n${query}\n${timestamp}\n${nonce}`
  const sign = await hmacSha256Base64(source, signSecret)

  return {
    'X-Timestamp': timestamp,
    'X-Nonce': nonce,
    'X-Sign': sign
  }
}
